import type { FeedItem } from "../../types/content/feedItem";
import type {
  InjectableComponent,
  InjectableResources,
  InjectableSlot,
  InjectableSnapshot,
  Placeholder,
  PlaceholderSubTypes,
} from "../../types/injectables";
import type { FeedNames } from "../../types/webTypes";

import { deepClone } from "../deepClone";

type InjectableHandlerInitalizerObj = {
  injectablesResources: InjectableResources | null;
  injectableSlotsList: InjectableSlot[];
  lastInjectedAtIndex?: number;
  feedName?: FeedNames;
  initialFeedWeight?: number;
  initialFeedWeightApplied?: boolean;
  currentResource: InjectableComponent | null;
  currentPositionIsUsed?: boolean;
  user: {
    isLoggedIn: boolean;
    isPremium: boolean;
  };
};

type TypeGuardInput = Placeholder | FeedItem;

const isPlaceholderType = (type: string): type is PlaceholderSubTypes =>
  type === "xandr" || type === "xandrNative" || type === "xandrNativeBackfill";

const isPlaceholder = (
  component: TypeGuardInput,
  type: string,
): component is Placeholder => {
  return (
    isPlaceholderType(type) &&
    "resources" in component &&
    Array.isArray((component as any).resources)
  );
};

const isBox = (
  _: TypeGuardInput,
  type: "native" | "box" | PlaceholderSubTypes,
): _ is FeedItem => {
  return type === "box";
};

const isNative = (
  _: TypeGuardInput,
  type: "native" | "box" | PlaceholderSubTypes,
): _ is FeedItem => {
  return type === "native";
};

class InjectableHandler {
  boxesList;
  placeholderList;
  nativeAdsList;
  injectableSlotsList;
  currentPosition;
  feedName;
  lastInjectedAtIndex;
  initialFeedWeight;
  initialFeedWeightApplied;
  currentPositionIsUsed = false;
  currentResource: InjectableComponent | null = null;
  isAllSlotsUsed = false;
  user: {
    isLoggedIn: boolean;
    isPremium: boolean;
  };

  constructor({
    injectablesResources,
    injectableSlotsList,
    lastInjectedAtIndex,
    feedName,
    initialFeedWeight,
    initialFeedWeightApplied,
    currentResource,
    currentPositionIsUsed,
    user,
  }: InjectableHandlerInitalizerObj) {
    this.injectableSlotsList = injectableSlotsList;
    this.boxesList = injectablesResources ? injectablesResources["boxes"] : [];
    this.placeholderList = injectablesResources
      ? injectablesResources["placeholders"]
      : [];
    this.nativeAdsList = injectablesResources
      ? injectablesResources["native-ads"]
      : [];
    this.currentPosition = injectableSlotsList[0];

    this.feedName = feedName;

    this.lastInjectedAtIndex = lastInjectedAtIndex || 0;

    this.initialFeedWeight = initialFeedWeight || 0;
    this.initialFeedWeightApplied = initialFeedWeightApplied || false;
    this.currentResource = currentResource ?? null;
    this.currentPositionIsUsed =
      currentPositionIsUsed ?? this.currentResource !== null;
    this.isAllSlotsUsed =
      !Array.isArray(injectableSlotsList) || injectableSlotsList.length === 0;
    this.user = user || {
      isLoggedIn: false,
      isPremium: false,
    };
  }

  private assignResource(resource: InjectableComponent) {
    this.currentResource = resource;
    this.currentPositionIsUsed = true;
  }

  private getType(
    type: string,
  ):
    | { type: PlaceholderSubTypes; resources: Placeholder[] }
    | { type: "native"; resources: FeedItem[] }
    | { type: "box"; resources: FeedItem[] }
    | { type: null; resources: null } {
    if (isPlaceholderType(type)) {
      return {
        type: type,
        resources: this.placeholderList.filter(
          (xandrType) => xandrType.type === type,
        ),
      };
    }

    if (type === "native") {
      return { type: "native", resources: this.nativeAdsList };
    }

    if (type === "box") {
      return { type: "box", resources: this.boxesList };
    }

    return { type: null, resources: null };
  }

  getLastInjectedAtIndex() {
    return this.lastInjectedAtIndex;
  }

  setLastInjectedAtIndex(index: number) {
    this.lastInjectedAtIndex = index;
  }

  nextPosition() {
    const newPositionConfigList = this.injectableSlotsList.slice(1);
    this.injectableSlotsList = newPositionConfigList;
    this.currentPosition = newPositionConfigList[0] || null;
  }

  getAvailableResource(): InjectableComponent | null {
    if (!this.currentPosition) {
      this.isAllSlotsUsed = true;
      return null;
    }

    if (this.currentPositionIsUsed && this.currentResource) {
      return this.currentResource;
    }
    const found = this.currentPosition.prio.some((prioType) => {
      if (this.currentPositionIsUsed) {
        return true;
      }

      const { type, resources } = this.getType(prioType);

      if (!type || !Array.isArray(resources)) {
        return false;
      }

      if (
        this.currentResource &&
        this.currentResource.type.toLowerCase() === type
      ) {
        console.log(
          `found resource for ${this.currentPosition.type} with prio ${this.currentPosition.prio}. Current resource is ${this.currentResource.type}`,
        );
        return this.currentResource;
      }

      return resources.some((item, i, arr) => {
        const copyOfArr = deepClone(arr);

        if (isPlaceholder(item, type) && item.resources.length > 0) {
          this.assignResource({
            type: "placeholder",
            subType: item.type,
            resource: item.resources[0],
            user: this.user,
          });

          const indexOfType = this.placeholderList.findIndex(
            (item) => item.type === type,
          );

          (copyOfArr[i] as Placeholder).resources = item.resources.slice(1);
          this.placeholderList = this.placeholderList.map((item, index) =>
            index === indexOfType ? (copyOfArr[i] as Placeholder) : item,
          );

          return true;
        }

        if (isBox(item, type)) {
          this.assignResource(item);
          this.boxesList = copyOfArr.slice(i + 1) as FeedItem[];
          return true;
        }

        if (isNative(item, type)) {
          this.assignResource(item);
          this.nativeAdsList = copyOfArr.slice(i + 1) as FeedItem[];

          return true;
        }

        return false;
      });
    });

    if (found && this.currentResource) {
      return this.currentResource;
    }

    return null;
  }

  getCurrentPosition() {
    return this.currentPosition;
  }

  getInitalOffsetForFeed() {
    return this.initialFeedWeight;
  }

  getInitialFeedWeightApplied() {
    return this.initialFeedWeightApplied;
  }

  getIsAllSlotsUsed() {
    return this.isAllSlotsUsed;
  }

  setInitialFeedWeightApplied(isApplied: boolean) {
    this.initialFeedWeightApplied = isApplied;
  }

  setCurrentPositionIsUsed(isUsed: boolean) {
    this.currentPositionIsUsed = isUsed;
    this.currentResource = null;
  }

  setCurrentPosition(index: number) {
    this.currentPosition = this.injectableSlotsList[index];
  }

  getCurrentDensityScore() {
    return this.currentPosition.densityScore;
  }

  getInjectablesSnapshot(): InjectableSnapshot {
    return {
      resources: {
        "native-ads": this.nativeAdsList,
        boxes: this.boxesList,
        placeholders: this.placeholderList,
      },
      slots: this.injectableSlotsList,
      lastInjectedAtIndex: this.lastInjectedAtIndex,
      initialFeedWeight: this.initialFeedWeight,
      initialFeedWeightApplied: this.initialFeedWeightApplied,
      feedName: this.feedName,
      currentResource: this.currentResource,
      currentPositionIsUsed: this.currentPositionIsUsed,
      user: this.user,
    };
  }
}

export { InjectableHandler };
